import { Card, CardContent, CardHeader, Grid } from '@mui/material';
import React from 'react';
import { UseQueryResult } from 'react-query';
import { useRecoilValue } from 'recoil';
import { env, miningPoolsState, useAllPoolAPIs, useUsername } from '../../api';
import { AccountMiningHistory, AccountProfile } from './PoolDetails';
import { useTranslation } from 'react-i18next';

export default function MultiPoolDashboard() {
  const miningPoolValues = useRecoilValue(miningPoolsState);
  const allPoolAPIs = useAllPoolAPIs();
  const validMiningPoolValues = miningPoolValues.filter(({ name }) => !!allPoolAPIs[name]);

  const userProfileQueries = validMiningPoolValues.map(({ name }) => allPoolAPIs[name].useUserProfile());
  const userMiningQueries = validMiningPoolValues.map(({ name }) => allPoolAPIs[name].useUserMiningInfos());

  const userProfileQueriesPayload = userProfileQueries.map((q: UseQueryResult<any, unknown>) =>
    !q.isSuccess ? {} : q.data.payload
  );
  const userMiningQueriesPayload = userMiningQueries.map((q: UseQueryResult<any, unknown>) =>
    !q.isSuccess ? [] : q.data.payload
  );
  const { t } = useTranslation();

  const username = useUsername();
  const oldestCreatedAt = userProfileQueriesPayload
    .filter((p: any) => !!p.createdAt)
    .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0];
  const address = userProfileQueriesPayload.find((p: any) => !!p.address)?.address;
  const totalRedeemedBalance = userProfileQueriesPayload.reduce((r: any, q: any) => {
    return q.redeemedBalance ? r + Number(q.redeemedBalance / 1e7) : r;
  }, 0);
  const totalBalance = userProfileQueriesPayload.reduce((r: any, q: any) => {
    return q.balance ? r + Number(q.balance / 1e7) : r;
  }, 0);

  const userProfile = {
    username,
    createdAt: oldestCreatedAt?.createdAt,
    address: address,
    redeemedBalance: totalRedeemedBalance,
    balance: totalBalance,
  };

  const mergedUserMiningInfos = userMiningQueriesPayload
    .reduce((acc, payload) => {
      if (!payload) return acc;
      return [...acc, ...payload];
    }, [])
    .sort((a: any, b: any) => b.startHeight - a.startHeight)
    .slice(0, 100);

  React.useEffect(() => {
    document.title = env.title + ` | ${t('navigatorPage.dashboard')}`;
  }, [t]);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Card>
          <CardHeader title={t('multiPoolDashboardPage.accountProfile')} />
          <CardContent sx={{ overflow: 'auto' }}>
            <AccountProfile userProfile={userProfile} balanceDivider={1} />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader title={t('multiPoolDashboardPage.latestAccountHistory')} />
          <CardContent sx={{ overflow: 'auto', maxHeight: '35vh', minHeight: '300px' }}>
            <AccountMiningHistory userMiningInfos={mergedUserMiningInfos} />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
